import React, { createContext, useCallback, useContext } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { Link, Page } from '../components'
import {
  selectHasCartSaveError,
  selectIsCartSaving,
} from '../../common/bulkOrder'
import BulkOrderCartSaveBanner from './BulkOrderCartSaveBanner'
import useBulkOrderCartSync from './useBulkOrderCartSync'
import useBulkOrderCatalogSync from './useBulkOrderCatalogSync'
import useBulkOrderCatalogCartNotice from './useBulkOrderCatalogCartNotice'
import { useIsBulkOrderEnabled } from './useIsBulkOrderEnabled'

const BulkOrderCartSyncContext = createContext({ flushCartSave: () => {} })

export const useBulkOrderCartSyncContext = () => useContext(BulkOrderCartSyncContext)

const BulkOrderPageShellInner = ({
  metaTitle,
  fillViewport = false,
  compactFooter = false,
  children,
}) => {
  const hasCartSaveError = useSelector(selectHasCartSaveError)
  const isCartSaving = useSelector(selectIsCartSaving)
  const bulkOrderGridFullWidth = useSelector(state => state.app.bulkOrderGridFullWidth)
  const { flushCartSave } = useBulkOrderCartSync()
  useBulkOrderCatalogSync()
  useBulkOrderCatalogCartNotice()

  const handleRetryCartSave = useCallback(() => {
    flushCartSave()
  }, [flushCartSave])

  return (
    <BulkOrderCartSyncContext.Provider value={{ flushCartSave }}>
      <Page metaTitle={metaTitle} fillViewport={fillViewport} compactFooter={compactFooter}>
        <Page.Content
          fillViewport={fillViewport}
          compactFooter={compactFooter}
          style={{
            maxWidth: fillViewport && bulkOrderGridFullWidth ? '100%' : undefined,
            transition: '0.3s',
          }}
        >
          <BulkOrderCartSaveBanner
            visible={hasCartSaveError}
            isRetryDisabled={isCartSaving}
            onRetrySave={handleRetryCartSave}
          />
          {children}
        </Page.Content>
      </Page>
    </BulkOrderCartSyncContext.Provider>
  )
}

BulkOrderPageShellInner.propTypes = {
  metaTitle: PropTypes.string.isRequired,
  fillViewport: PropTypes.bool,
  compactFooter: PropTypes.bool,
  children: PropTypes.node.isRequired,
}

const BulkOrderPageShell = ({
  metaTitle,
  fillViewport = false,
  compactFooter = false,
  children,
}) => {
  const isBulkOrderEnabledForUser = useIsBulkOrderEnabled()

  if (!isBulkOrderEnabledForUser) {
    return <Navigate to={Link.HOME} replace />
  }

  return (
    <BulkOrderPageShellInner
      metaTitle={metaTitle}
      fillViewport={fillViewport}
      compactFooter={compactFooter}
    >
      {children}
    </BulkOrderPageShellInner>
  )
}

BulkOrderPageShell.propTypes = {
  metaTitle: PropTypes.string.isRequired,
  fillViewport: PropTypes.bool,
  compactFooter: PropTypes.bool,
  children: PropTypes.node.isRequired,
}

export default BulkOrderPageShell
